import * as React from 'react';
import { cn } from '@/lib/utils';

// Enhanced mobile detection hook
export const useMobileDetection = () => {
  const [isMobile, setIsMobile] = React.useState(false);
  const [isTablet, setIsTablet] = React.useState(false);
  const [isTouch, setIsTouch] = React.useState(false);
  const [orientation, setOrientation] = React.useState<'portrait' | 'landscape'>('portrait');

  React.useEffect(() => {
    const checkDevice = () => {
      // Batch reads in requestAnimationFrame to prevent forced reflow
      requestAnimationFrame(() => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        
        setIsMobile(width < 768);
        setIsTablet(width >= 768 && width < 1024);
        setIsTouch('ontouchstart' in window || navigator.maxTouchPoints > 0);
        setOrientation(height > width ? 'portrait' : 'landscape');
      });
    };

    checkDevice();
    
    // Throttle resize handler with RAF
    let rafId: number | null = null;
    const handleResize = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        checkDevice();
        rafId = null;
      });
    };
    
    const handleOrientationChange = () => {
      // Delay to get accurate dimensions after orientation change
      setTimeout(checkDevice, 100);
    };

    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  return { isMobile, isTablet, isTouch, orientation };
};

// Enhanced mobile-friendly container
interface MobileContainerProps {
  children: React.ReactNode;
  className?: string;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  padding?: 'sm' | 'md' | 'lg';
}

export const MobileContainer = ({ 
  children, 
  className,
  maxWidth = 'xl',
  padding = 'md'
}: MobileContainerProps) => {
  const { isMobile } = useMobileDetection();

  const maxWidthClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg', 
    xl: 'max-w-7xl',
    full: 'max-w-full'
  };

  const paddingClasses = {
    sm: isMobile ? 'px-4' : 'px-6',
    md: isMobile ? 'px-4' : 'px-6 lg:px-12',
    lg: isMobile ? 'px-6' : 'px-8 lg:px-16'
  };

  return (
    <div className={cn(
      'container mx-auto',
      maxWidthClasses[maxWidth],
      paddingClasses[padding],
      className
    )}>
      {children}
    </div>
  );
};

// Enhanced mobile-friendly button
interface MobileButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?: 'default' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}

export const MobileButton = ({ 
  children, 
  className, 
  variant = 'default',
  size = 'md',
  fullWidth = false,
  ...props 
}: MobileButtonProps) => {
  const { isMobile, isTouch } = useMobileDetection();

  const sizeClasses = {
    sm: isMobile ? 'min-h-[44px] px-4 text-sm' : 'h-9 px-3 text-sm',
    md: isMobile ? 'min-h-[48px] px-6 text-base' : 'h-10 px-4 text-sm',
    lg: isMobile ? 'min-h-[52px] px-8 text-lg' : 'h-11 px-8 text-base'
  };

  const touchClasses = isTouch ? 'touch-manipulation active:scale-95' : '';

  return (
    <button 
      className={cn(
        'inline-flex items-center justify-center rounded-md font-medium transition-all duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        sizeClasses[size],
        touchClasses,
        fullWidth && 'w-full',
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
};

// Mobile-optimized grid component
interface MobileGridProps {
  children: React.ReactNode;
  className?: string;
  cols?: 1 | 2 | 3 | 4;
  gap?: 'sm' | 'md' | 'lg';
  responsive?: boolean;
}

export const MobileGrid = ({ 
  children, 
  className,
  cols = 2,
  gap = 'md',
  responsive = true 
}: MobileGridProps) => {
  const { isMobile, isTablet } = useMobileDetection();

  const getGridCols = () => {
    if (!responsive) return `grid-cols-${cols}`;
    
    if (isMobile) {
      return cols > 2 ? 'grid-cols-1' : `grid-cols-${Math.min(cols, 2)}`;
    }
    
    if (isTablet) {
      return `grid-cols-${Math.min(cols, 3)}`;
    }
    
    return `grid-cols-${cols}`;
  };

  const gapClasses = {
    sm: 'gap-2',
    md: 'gap-4',
    lg: 'gap-6'
  };

  return (
    <div className={cn(
      'grid',
      getGridCols(),
      gapClasses[gap],
      className
    )}>
      {children}
    </div>
  );
};

// Mobile-optimized text component
interface MobileTextProps {
  children: React.ReactNode;
  className?: string;
  size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl';
  responsive?: boolean;
}

export const MobileText = ({ 
  children, 
  className,
  size = 'base',
  responsive = true 
}: MobileTextProps) => {
  const { isMobile } = useMobileDetection();

  const responsiveSizes = {
    xs: responsive && isMobile ? 'text-xs' : 'text-xs',
    sm: responsive && isMobile ? 'text-sm' : 'text-sm',
    base: responsive && isMobile ? 'text-sm' : 'text-base',
    lg: responsive && isMobile ? 'text-base' : 'text-lg',
    xl: responsive && isMobile ? 'text-lg' : 'text-xl',
    '2xl': responsive && isMobile ? 'text-xl' : 'text-2xl',
    '3xl': responsive && isMobile ? 'text-2xl' : 'text-3xl'
  };

  return (
    <span className={cn(
      responsiveSizes[size],
      'leading-relaxed',
      className
    )}>
      {children}
    </span>
  );
};

// Mobile scroll optimization hook
export const useMobileScroll = () => {
  const [isScrolling, setIsScrolling] = React.useState(false);
  
  const smoothScrollTo = React.useCallback((elementId: string, offset: number = 0) => {
    const element = document.getElementById(elementId);
    if (element) {
      // Separate DOM reads from writes across RAF frames
      requestAnimationFrame(() => {
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset - offset;
        
        // Write in next frame to prevent forced reflow
        requestAnimationFrame(() => {
          window.scrollTo({
            top: elementPosition,
            behavior: 'smooth'
          });
        });
      });
    }
  }, []);

  React.useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      setIsScrolling(true);
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        setIsScrolling(false);
      }, 150);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  return { isScrolling, smoothScrollTo };
};

// Performance optimization for mobile
export const useMobilePerformance = () => {
  React.useEffect(() => {
    // Batch layout read in RAF to prevent forced reflow
    requestAnimationFrame(() => {
      const isMobile = window.innerWidth < 768;
      
      if (isMobile) {
        // Disable animations on very slow devices
        const connection = (navigator as any).connection;
        if (connection && connection.effectiveType === 'slow-2g') {
          document.body.classList.add('reduce-animations');
        }

        // Optimize scroll performance
        document.body.style.overscrollBehavior = 'none';
        
        // Prevent zoom on input focus
        const viewport = document.querySelector('meta[name=viewport]');
        if (viewport) {
          viewport.setAttribute('content', 
            'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
          );
        }
      }
    });

    return () => {
      document.body.style.overscrollBehavior = '';
    };
  }, []);
};